import React, { useEffect } from 'react';
import {
  LuMail,
  LuGraduationCap,
  LuUsers,
  LuArrowLeft,
  LuCheck,
  LuAlertTriangle,
  LuClock
} from 'react-icons/lu';

const ReleaseMethodSelection = ({
  releaseAnalysis,
  selectedReleaseMethod,
  setSelectedReleaseMethod,
  selectedStudents,
  onBack,
  onContinue,
  isLoading
}) => {
  const isComingSoon = method => {
    return method === 'google_classroom' || method === 'google_classroom_plus_email';
  };

  // Force email selection if current selection is a coming soon option
  useEffect(() => {
    if (releaseAnalysis && selectedReleaseMethod && isComingSoon(selectedReleaseMethod)) {
      setSelectedReleaseMethod('email');
    } else if (releaseAnalysis && !selectedReleaseMethod) {
      // Default to email if no method is selected
      setSelectedReleaseMethod('email');
    }
  }, [releaseAnalysis, selectedReleaseMethod, setSelectedReleaseMethod]);

  if (!releaseAnalysis) {
    return null;
  }

  const getScenarioTitle = () => {
    switch (releaseAnalysis.scenario) {
      case 'all_google_classroom':
        return 'All submissions from Google Classroom';
      case 'mixed':
        return 'Mixed submission sources';
      case 'no_google_classroom':
        return 'No Google Classroom submissions';
      default:
        return 'Release method selection';
    }
  };

  const getScenarioDescription = () => {
    switch (releaseAnalysis.scenario) {
      case 'all_google_classroom':
        return 'All students submitted through Google Classroom. Grades will be posted directly where students submitted their work.';
      case 'mixed':
        return `${releaseAnalysis.google_classroom_count} students submitted via Google Classroom, ${releaseAnalysis.total_submissions - releaseAnalysis.google_classroom_count} students submitted through other methods.`;
      case 'no_google_classroom':
        return 'All students submitted through other methods. Grades will be sent via email.';
      default:
        return '';
    }
  };

  const getMethodIcon = method => {
    switch (method) {
      case 'google_classroom':
        return <LuGraduationCap className="w-5 h-5" />;
      case 'email':
        return <LuMail className="w-5 h-5" />;
      case 'google_classroom_plus_email':
        return <LuUsers className="w-5 h-5" />;
      default:
        return <LuMail className="w-5 h-5" />;
    }
  };

  const getMethodTitle = method => {
    switch (method) {
      case 'google_classroom':
        return 'Release to Google Classroom';
      case 'email':
        return 'Release via Email';
      case 'google_classroom_plus_email':
        return 'Release to Google Classroom + Email';
      default:
        return 'Release via Email';
    }
  };

  const getMethodDescription = method => {
    switch (method) {
      case 'google_classroom':
        return 'Post grades and feedback directly to Google Classroom where students submitted their work.';
      case 'email':
        return 'Send grades and feedback via email to all students. This ensures everyone receives their results.';
      case 'google_classroom_plus_email':
        return 'Post to Google Classroom where possible, send email to students who submitted through other methods.';
      default:
        return 'Send grades and feedback via email to all students.';
    }
  };

  const isRecommended = method => {
    return method === releaseAnalysis.recommended_method;
  };

  const getWarningMessage = () => {
    if (releaseAnalysis.scenario === 'mixed' && selectedReleaseMethod === 'google_classroom') {
      return 'Warning: Some students will not receive grades if you choose Google Classroom only.';
    }
    return null;
  };

  const handleMethodChange = (method) => {
    // Prevent selection of coming soon options
    if (!isComingSoon(method)) {
      setSelectedReleaseMethod(method);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                onClick={onBack}
              >
                <LuArrowLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-semibold text-gray-800">Choose Release Method</h2>
            </div>
            <div className="text-sm text-gray-600">
              {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''} selected
            </div>
          </div>

          {/* Scenario Summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-800 mb-2">{getScenarioTitle()}</h3>
            <p className="text-sm text-gray-600 mb-3">{getScenarioDescription()}</p>

            {releaseAnalysis.scenario === 'mixed' && (
              <div className="flex items-center gap-2 text-amber-600 text-sm">
                <LuAlertTriangle className="w-4 h-4" />
                <span>
                  To ensure ALL students receive feedback, we recommend using email for everyone.
                </span>
              </div>
            )}
          </div>

          {/* Release Method Options */}
          <div className="space-y-3 mb-6">
            {releaseAnalysis.available_methods.map(method => (
              <div key={method} className="relative">
                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg transition-all ${isComingSoon(method) ? 'opacity-60 cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-gray-50'}`}>
                  <input
                    type="radio"
                    name="releaseMethod"
                    value={method}
                    checked={selectedReleaseMethod === method}
                    disabled={isComingSoon(method)}
                    className="mt-1 w-4 h-4 text-orange-600 border-gray-300 focus:ring-orange-500 disabled:opacity-50"
                    onChange={e => handleMethodChange(e.target.value)}
                  />

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {getMethodIcon(method)}
                      <span className="font-medium text-gray-800">{getMethodTitle(method)}</span>
                      {isRecommended(method) && !isComingSoon(method) && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          <LuCheck className="w-3 h-3" />
                          Recommended
                        </span>
                      )}
                      {isComingSoon(method) && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          <LuClock className="w-3 h-3" />
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {isComingSoon(method)
                        ? 'Google Classroom integration is currently being reviewed for approval. Use email for now.'
                        : getMethodDescription(method)
                      }
                    </p>
                  </div>
                </label>
              </div>
            ))}
          </div>

          {/* Warning Message */}
          {getWarningMessage() && (
            <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-800">
                <LuAlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">{getWarningMessage()}</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <button
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              onClick={onBack}
            >
              Back to Selection
            </button>

            <button
              disabled={isLoading}
              className="px-6 py-2 bg-gradient-to-r from-[#e4b17a] to-[#c99b69] text-white rounded-md hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onContinue}
            >
              {isLoading
                ? 'Releasing...'
                : `Release to ${selectedStudents.length} Student${selectedStudents.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReleaseMethodSelection;
